import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { Drawer, Modal, message } from "antd";
import mergeClassNames from "common/mergeClassNames";
import AttachmentThumbnail from "components/atoms/AttachmentThumbnail";
import useAttachmentViewerPreference from "hooks/useAttachmentViewerPreference";
import useInactivityTimeout from "hooks/useInactivityTimeout";
import { getUrl } from "aws-amplify/storage";
import { useCallback, useEffect, useMemo, useState } from "react";
import AttachmentContent from "./AttachmentContent";
import AttachmentToolbar from "./AttachmentToolbar";
import FloatingWindow from "./FloatingWindow";
import { VIEWER_MODES, getFileCategory, isMaskedValue } from "./utils";

const DEFAULT_PII_TIMEOUT_MS = 60000;

/**
 * Unified attachment viewing experience (PRE-1516).
 *
 * One component renders the same content + controls across four interchangeable
 * modes — full-screen overlay, centred modal, side panel (drawer) and a
 * draggable/resizable floating window — with a carousel, thumbnail strip,
 * image zoom/rotate, inline rendering for images / PDF / Office / text, an
 * optional PII inactivity auto-close, and a remembered default mode.
 */
const AttachmentViewer = ({
    visible,
    files = [],
    currentIndex = 0,
    onClose,
    onDownload,
    mode: controlledMode,
    defaultMode = "overlay",
    showModeSwitcher = true,
    piiProtected = false,
    inactivityTimeoutMs = DEFAULT_PII_TIMEOUT_MS,
    className = "",
}) => {
    const [index, setIndex] = useState(currentIndex);
    const [loading, setLoading] = useState(false);
    const [url, setUrl] = useState(null);
    const [zoom, setZoom] = useState(1);
    const [rotation, setRotation] = useState(0);
    const [position, setPosition] = useState({ x: 0, y: 0 });
    const [isDragging, setIsDragging] = useState(false);
    const [dragStart, setDragStart] = useState({ x: 0, y: 0 });

    // Mode: optionally controlled; otherwise backed by the remembered preference.
    const [storedMode, setStoredMode] = useAttachmentViewerPreference(defaultMode);
    const [sessionMode, setSessionMode] = useState(null);
    const [remember, setRemember] = useState(true);
    const mode = controlledMode || sessionMode || storedMode;
    const allowSwitch = !controlledMode && showModeSwitcher;

    const currentFile = files[index];
    const masked = isMaskedValue(currentFile?.value) || currentFile?.masked === true;
    const isImage = getFileCategory(currentFile) === "image";

    useEffect(() => {
        setIndex(currentIndex);
    }, [currentIndex]);

    const resetTransforms = useCallback(() => {
        setZoom(1);
        setRotation(0);
        setPosition({ x: 0, y: 0 });
    }, []);

    // Load the pre-signed URL for the current file (any type — image, pdf, etc.).
    useEffect(() => {
        let cancelled = false;
        const load = async () => {
            if (!visible || !currentFile?.key || masked) {
                setUrl(null);
                return;
            }
            setLoading(true);
            try {
                const result = await getUrl({ path: `public/${currentFile.key}` });
                if (!cancelled) setUrl(result.url.toString());
            } catch (error) {
                console.error("Error loading attachment:", error);
                if (!cancelled) {
                    setUrl(null);
                    message.error("Failed to load attachment");
                }
            } finally {
                if (!cancelled) setLoading(false);
            }
        };
        load();
        resetTransforms();
        return () => {
            cancelled = true;
        };
    }, [visible, currentFile, masked, resetTransforms]);

    // ---- Carousel navigation ------------------------------------------------
    const nextFile = useCallback(() => {
        if (files.length <= 1) return;
        setIndex((prev) => (prev + 1) % files.length);
    }, [files.length]);

    const prevFile = useCallback(() => {
        if (files.length <= 1) return;
        setIndex((prev) => (prev - 1 + files.length) % files.length);
    }, [files.length]);

    // ---- Image transforms ---------------------------------------------------
    const handleZoomIn = useCallback(() => setZoom((p) => Math.min(p * 1.2, 5)), []);
    const handleZoomOut = useCallback(() => setZoom((p) => Math.max(p / 1.2, 0.1)), []);
    const handleRotateLeft = useCallback(() => setRotation((p) => p - 90), []);
    const handleRotateRight = useCallback(() => setRotation((p) => p + 90), []);

    const handleMouseDown = useCallback(
        (e) => {
            if (!isImage || zoom <= 1) return;
            setIsDragging(true);
            setDragStart({ x: e.clientX - position.x, y: e.clientY - position.y });
        },
        [isImage, zoom, position],
    );

    // ---- Download / open ----------------------------------------------------
    const handleDownload = useCallback(async () => {
        if (!currentFile?.key) return;
        try {
            const result = await getUrl({ path: `public/${currentFile.key}` });
            const href = result.url.toString();
            const link = document.createElement("a");
            link.href = href;
            link.download = currentFile.name || "attachment";
            document.body.appendChild(link);
            link.click();
            document.body.removeChild(link);
            onDownload?.(currentFile);
        } catch (error) {
            console.error("Download failed:", error);
            message.error("Download failed");
        }
    }, [currentFile, onDownload]);

    const handleOpenNewTab = useCallback(async () => {
        if (url) {
            window.open(url, "_blank", "noopener,noreferrer");
            return;
        }
        if (!currentFile?.key) return;
        try {
            const result = await getUrl({ path: `public/${currentFile.key}` });
            window.open(result.url.toString(), "_blank", "noopener,noreferrer");
        } catch (error) {
            console.error("Open failed:", error);
            message.error("Failed to open attachment");
        }
    }, [url, currentFile]);

    // ---- Mode switching -----------------------------------------------------
    const handleModeChange = useCallback(
        (next) => {
            if (!VIEWER_MODES.includes(next)) return;
            setSessionMode(next);
            if (remember) setStoredMode(next);
        },
        [remember, setStoredMode],
    );

    // ---- PII inactivity auto-close -----------------------------------------
    const { secondsRemaining } = useInactivityTimeout(visible && piiProtected, onClose, inactivityTimeoutMs);

    // ---- Global mouse drag + keyboard --------------------------------------
    useEffect(() => {
        if (!visible) return undefined;

        const handleMouseMove = (e) => {
            if (!isDragging || zoom <= 1) return;
            setPosition({ x: e.clientX - dragStart.x, y: e.clientY - dragStart.y });
        };
        const handleMouseUp = () => setIsDragging(false);
        const handleKeyDown = (e) => {
            switch (e.key) {
                case "ArrowLeft":
                    prevFile();
                    break;
                case "ArrowRight":
                    nextFile();
                    break;
                case "Escape":
                    onClose?.();
                    break;
                case "+":
                case "=":
                    if (isImage) handleZoomIn();
                    break;
                case "-":
                    if (isImage) handleZoomOut();
                    break;
                case "0":
                    if (isImage) resetTransforms();
                    break;
                default:
                    break;
            }
        };

        document.addEventListener("mousemove", handleMouseMove);
        document.addEventListener("mouseup", handleMouseUp);
        document.addEventListener("keydown", handleKeyDown);
        return () => {
            document.removeEventListener("mousemove", handleMouseMove);
            document.removeEventListener("mouseup", handleMouseUp);
            document.removeEventListener("keydown", handleKeyDown);
        };
    }, [visible, isDragging, zoom, dragStart, prevFile, nextFile, onClose, isImage, handleZoomIn, handleZoomOut, resetTransforms]);

    const availableModes = useMemo(() => (allowSwitch ? VIEWER_MODES : []), [allowSwitch]);

    const renderToolbar = (tone) => (
        <AttachmentToolbar
            file={currentFile}
            index={index}
            total={files.length}
            isImage={isImage && !masked}
            tone={tone}
            onZoomIn={handleZoomIn}
            onZoomOut={handleZoomOut}
            onRotateLeft={handleRotateLeft}
            onRotateRight={handleRotateRight}
            onReset={resetTransforms}
            onDownload={handleDownload}
            onOpenNewTab={handleOpenNewTab}
            onClose={onClose}
            mode={mode}
            onModeChange={handleModeChange}
            availableModes={availableModes}
            remember={remember}
            onToggleRemember={setRemember}
            piiProtected={piiProtected}
            secondsRemaining={secondsRemaining}
        />
    );

    const renderThumbnails = () => {
        if (files.length <= 1) return null;
        return (
            <div className="w-full overflow-x-auto">
                <div className="flex gap-2 px-1 py-1">
                    {files.map((f, i) => (
                        <button
                            key={f.key || i}
                            onClick={() => setIndex(i)}
                            aria-label={`View ${f.name || `attachment ${i + 1}`}`}
                            className={mergeClassNames(
                                "shrink-0 rounded-lg ring-1 ring-gray-300 transition-all",
                                i === index ? "ring-2 ring-blue-500" : "opacity-70 hover:opacity-100",
                            )}
                        >
                            <AttachmentThumbnail file={f} size="small" lazy={false} className="!m-0 p-1" />
                        </button>
                    ))}
                </div>
            </div>
        );
    };

    const renderNavArrows = (tone) => {
        if (files.length <= 1) return null;
        const base =
            tone === "dark"
                ? "bg-black/60 text-white hover:bg-black/80"
                : "bg-white text-gray-700 shadow ring-1 ring-gray-200 hover:bg-gray-50";
        return (
            <>
                <button
                    onClick={prevFile}
                    aria-label="Previous"
                    className={mergeClassNames("absolute left-2 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full transition-all", base)}
                >
                    <LeftOutlined />
                </button>
                <button
                    onClick={nextFile}
                    aria-label="Next"
                    className={mergeClassNames("absolute right-2 top-1/2 z-10 flex h-10 w-10 -translate-y-1/2 items-center justify-center rounded-full transition-all", base)}
                >
                    <RightOutlined />
                </button>
            </>
        );
    };

    const renderContent = (tone) => (
        <AttachmentContent
            file={currentFile}
            url={url}
            loading={loading}
            masked={masked}
            tone={tone}
            zoom={zoom}
            rotation={rotation}
            position={position}
            isDragging={isDragging}
            onMouseDown={handleMouseDown}
            onDownload={handleDownload}
            onOpenNewTab={handleOpenNewTab}
        />
    );

    if (!visible || files.length === 0) return null;

    // ---- Mode wrappers ------------------------------------------------------
    if (mode === "overlay") {
        return (
            <div
                className={mergeClassNames("fixed inset-0 z-[1000] flex flex-col bg-black/90", className)}
                style={{ animation: "attViewerFadeIn 0.2s ease-in-out" }}
                role="dialog"
                aria-modal="true"
                aria-label="Attachment viewer"
            >
                <div className="z-10 px-4 py-3">
                    <div className="rounded-lg bg-black/60 px-4 py-2 backdrop-blur-sm">{renderToolbar("dark")}</div>
                </div>
                <div className="relative flex flex-1 items-center justify-center overflow-hidden p-4 sm:p-8">
                    {renderNavArrows("dark")}
                    <div className="h-full w-full">{renderContent("dark")}</div>
                </div>
                <div className="z-10 bg-black/40 px-4 py-2">{renderThumbnails()}</div>
                <style>{`@keyframes attViewerFadeIn { from { opacity: 0; } to { opacity: 1; } }`}</style>
            </div>
        );
    }

    if (mode === "drawer") {
        return (
            <Drawer
                open={visible}
                onClose={onClose}
                placement="right"
                width={Math.min(560, typeof window !== "undefined" ? window.innerWidth : 560)}
                title={null}
                closable={false}
                styles={{ body: { padding: 0, display: "flex", flexDirection: "column", height: "100%" } }}
                className={className}
            >
                <div className="border-b border-gray-100 px-4 py-3">{renderToolbar("light")}</div>
                <div className="relative flex-1 overflow-hidden bg-gray-100 p-2">
                    {renderNavArrows("light")}
                    {renderContent("light")}
                </div>
                <div className="border-t border-gray-100 px-3 py-2">{renderThumbnails()}</div>
            </Drawer>
        );
    }

    if (mode === "floating") {
        return (
            <FloatingWindow header={renderToolbar("light")} className={className}>
                <div className="flex h-full flex-col">
                    <div className="relative flex-1 overflow-hidden">
                        {renderNavArrows("light")}
                        {renderContent("light")}
                    </div>
                    <div className="border-t border-gray-100 bg-white px-2 py-1">{renderThumbnails()}</div>
                </div>
            </FloatingWindow>
        );
    }

    // Default: modal
    return (
        <Modal
            open={visible}
            onCancel={onClose}
            footer={null}
            closable={false}
            centered
            width="80%"
            styles={{ body: { padding: 0 } }}
            className={mergeClassNames("attachment-viewer-modal", className)}
        >
            <div className="flex flex-col" style={{ height: "80vh" }}>
                <div className="px-4 py-3">{renderToolbar("light")}</div>
                <div className="relative flex-1 overflow-hidden bg-gray-100">
                    {renderNavArrows("light")}
                    {renderContent("light")}
                </div>
                <div className="border-t border-gray-100 px-3 py-2">{renderThumbnails()}</div>
            </div>
        </Modal>
    );
};

export default AttachmentViewer;
